import { useContext, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { AuthContext } from '../../contexts/auth';
import { PaymentStep } from './PaymentStep';
import logo from '../../assets/logo-new.png';
import { PasswordInput } from '../../components/ui/password-input';
import { ArrowLeft } from 'lucide-react';

function getModeFromParam(mode) {
  if (mode === 'register' || mode === 'forgot-password') return mode;
  return 'login';
}

function getPlanFromParam(plan) {
  return plan === 'pro' ? 'pro' : 'free';
}

export default function SignIn() {
  const { signIn, signUp, forgot, loadingAuth, signed } = useContext(AuthContext);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [viewMode, setViewMode] = useState(() => getModeFromParam(searchParams.get('mode')));
  const [selectedPlan, setSelectedPlan] = useState(() => getPlanFromParam(searchParams.get('plan')));
  const [signupStep, setSignupStep] = useState('form'); // 'form' | 'payment'
  const [pendingRegistration, setPendingRegistration] = useState(null); // {nome,email,password}
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [fullName, setFullName] = useState('');
  const [rememberMe, setRememberMe] = useState(true);

  useEffect(() => {
    setViewMode(getModeFromParam(searchParams.get('mode')));
    setSelectedPlan(getPlanFromParam(searchParams.get('plan')));
  }, [searchParams]);

  useEffect(() => {
    // Redireciona usuarios ja logados — exceto quando estao no fluxo de pagamento
    if (signed && signupStep !== 'payment') {
      navigate('/dashboard', { replace: true });
    }
  }, [signed, signupStep, navigate]);

  const changeViewMode = (nextMode) => {
    const normalizedMode = getModeFromParam(nextMode);
    setViewMode(normalizedMode);
    const params = {};
    if (normalizedMode !== 'login') params.mode = normalizedMode;
    if (selectedPlan === 'pro') params.plan = 'pro';
    setSearchParams(params, { replace: true });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error(t('fillAllFields'));
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error(t('invalidEmail'));
      return;
    }
    if (password.length < 6) {
      toast.error(t('passwordMin6'));
      return;
    }
    await signIn(email, password, rememberMe);
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!fullName || !email || !password || !confirmPassword) {
      toast.error(t('fillAllFields'));
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error(t('invalidEmail'));
      return;
    }
    if (password.length < 6) {
      toast.error(t('passwordMin6'));
      return;
    }
    if (password !== confirmPassword) {
      toast.error(t('passwordsDoNotMatch'));
      return;
    }

    if (selectedPlan === 'pro') {
      // NAO cria conta ainda. Guarda os dados e avanca pro pagamento.
      setPendingRegistration({ nome: fullName, email, password });
      setSignupStep('payment');
      return;
    }

    // Free: cria conta direto
    await signUp(email, fullName, password);
    // useEffect detecta signed=true e redireciona pro dashboard
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error(t('enterYourEmail'));
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error(t('invalidEmail'));
      return;
    }
    try {
      await forgot(email);
      setTimeout(() => {
        changeViewMode('login');
        setPassword('');
      }, 2000);
    } catch {}
  };

  const handlePaymentSuccess = () => {
    // PaymentStep ja chamou setUser/storageUser; aqui so navega
    navigate('/dashboard', { replace: true });
  };

  const handleFallbackFree = () => {
    navigate('/dashboard', { replace: true });
  };

  const resetToLogin = () => {
    changeViewMode('login');
    setEmail('');
    setPassword('');
    setConfirmPassword('');
    setFullName('');
  };

  const getTitle = () => {
    if (signupStep === 'payment') return t('paymentStepLabel');
    switch (viewMode) {
      case 'register':
        return selectedPlan === 'pro' ? t('createAccountPro') : t('createAccount');
      case 'forgot-password':
        return t('recoverPassword');
      default:
        return t('signInHeroTitle');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-900 flex items-center justify-center p-4 relative overflow-hidden">
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full mix-blend-overlay filter blur-3xl animate-pulse" />
        <div
          className="absolute bottom-0 right-0 w-96 h-96 bg-cyan-200 rounded-full mix-blend-overlay filter blur-3xl animate-pulse"
          style={{ animationDelay: '1000ms' }}
        />
      </div>

      <div className="w-full max-w-md relative z-10">
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-700 p-8 text-center relative">
            {viewMode !== 'login' && signupStep !== 'payment' && (
              <button
                onClick={resetToLogin}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:text-blue-100 transition-colors cursor-pointer bg-transparent border-0"
                type="button"
              >
                <ArrowLeft className="w-6 h-6" />
              </button>
            )}
            <div className="flex justify-center mb-4">
              <img src={logo} alt="Investe Sempre" className="w-48 h-auto" />
            </div>
            <p className="text-blue-100 text-sm">{getTitle()}</p>
          </div>

          {signupStep === 'payment' ? (
            <PaymentStep
              pendingRegistration={pendingRegistration}
              onSuccess={handlePaymentSuccess}
              onFallbackToFree={handleFallbackFree}
            />
          ) : (
            <>
              {viewMode === 'login' && (
                <form onSubmit={handleSubmit} className="p-8 space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder={t('emailPlaceholder')}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="h-12"
                    />
                  </div>

                 <div className="space-y-2">
                        <Label htmlFor="password">
                          {t('password')}
                        </Label>
                      
                          <PasswordInput
                            id="password"
                            name="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            autoComplete="current-password"
                            ariaLabelShow={t('showPassword')}
                            ariaLabelHide={t('hidePassword')}
                          />                        
                      </div>

                  <div className="flex items-center justify-between text-sm">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        className="rounded border-gray-300"
                        checked={rememberMe}
                        onChange={(e) => setRememberMe(e.target.checked)}
                      />
                      <span className="text-gray-600">{t('rememberMe')}</span>
                    </label>
                    <button
                      type="button"
                      onClick={() => changeViewMode('forgot-password')}
                      className="text-blue-600 hover:text-blue-700 cursor-pointer bg-transparent border-0"
                    >
                      {t('forgotPassword')}
                    </button>
                  </div>

                  <Button
                    type="submit"
                    className="w-full h-12 bg-gradient-to-r from-blue-600 to-indigo-700 hover:from-blue-700 hover:to-indigo-800"
                    disabled={loadingAuth}
                  >
                    {loadingAuth ? t('loadingShort') : t('accessButton')}
                  </Button>

                  <div className="text-center space-y-4">
                    <button
                      type="button"
                      onClick={() => changeViewMode('register')}
                      className="text-sm text-gray-600 hover:text-gray-900 cursor-pointer bg-transparent border-0"
                    >
                      {t('dontHaveAccount')}{' '}
                      <span className="text-blue-600 font-medium">{t('signUp')}</span>
                    </button>

                  </div>
                </form>
              )}

              {viewMode === 'forgot-password' && (
                  <form
                    onSubmit={handleForgotPassword}
                    className="p-8 space-y-6"
                  >
                    <div className="text-center mb-4">
                        <p>{t('enterEmailToRecover')}</p>
                        <p>{t('newPasswordSentToEmail')}</p>
                      </div>

                    <div className="space-y-2">
                      <Label htmlFor="forgotEmail">
                        Email
                      </Label>                      

                      <Input
                        id="forgotEmail"
                        type="email"
                        placeholder={t('emailPlaceholder')}
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="h-12"
                      />
                    </div>

                    <Button
                      type="submit"
                      className="w-full h-12 bg-gradient-to-r from-blue-600 to-indigo-700 hover:from-blue-700 hover:to-indigo-800"
                      disabled={loadingAuth}
                    >
                      {loadingAuth
                        ? t('sendingShort')
                        : t('resetPassword')}
                    </Button>
                  </form>
                )}

              {viewMode === 'register' && (
                <form onSubmit={handleRegister} className="p-8 space-y-5">
                  {selectedPlan === 'pro' && (
                    <div className="rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-800">
                      {t('proPlanSelectedNote')}
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label htmlFor="fullName">{t('fullName')}</Label>
                    <Input
                      id="fullName"
                      type="text"
                      placeholder={t('fullNamePlaceholder')}
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      className="h-12"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="registerEmail">Email</Label>
                    <Input
                      id="registerEmail"
                      type="email"
                      placeholder={t('emailPlaceholder')}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="h-12"
                    />
                  </div>

                  <div className="space-y-2">
                      <Label htmlFor="registerPassword">
                        {t('password')}
                      </Label>

                     <PasswordInput
                      id="registerPassword"
                      name="new-password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      autoComplete="new-password"
                      ariaLabelShow={t('showPassword')}
                      ariaLabelHide={t('hidePassword')}
                    />
                    </div>

                   <div className="space-y-2">
                    <Label htmlFor="confirmPassword">
                      {t('confirmPassword')}
                    </Label>

                        <PasswordInput
                          id="confirmPassword"
                          name="confirm-password"
                          value={confirmPassword}
                          onChange={(e) =>
                            setConfirmPassword(e.target.value)
                          }
                          autoComplete="new-password"
                          ariaLabelShow={t('showPassword')}
                          ariaLabelHide={t('hidePassword')}
                        />
                         </div>
                      <Button
                        type="submit"
                        className="w-full h-12 bg-gradient-to-r from-blue-600 to-indigo-700 hover:from-blue-700 hover:to-indigo-800"
                        disabled={loadingAuth}
                      >
                        {loadingAuth
                          ? t('creatingShort')
                          : selectedPlan === 'pro'
                            ? t('continueToPayment')
                            : t('createAccount')}
                      </Button>                 
                </form>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
